import { Component, DestroyRef, effect, inject, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import {
    MatTableDataSource,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
} from '@angular/material/table';
import { faAngleRight } from '@fortawesome/free-solid-svg-icons';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { switchMap, filter, forkJoin } from 'rxjs';
import { SdaTargetTypes } from 'src/app/models/centrex/enums/sda-target-types';
import { Sda } from 'src/app/models/centrex/sda';
import { SdaService } from 'src/app/services/centrex/sda.service';
import { DialogDeleteConfirmationComponent } from '../../dialogs/dialog-delete-confirmation/dialog-delete-confirmation.component';
import { ExtensionService } from 'src/app/services/centrex/extension.service';
import { Extension } from 'src/app/models/centrex/extension';
import { ServiceService } from 'src/app/services/centrex/service.service';
import { Service } from 'src/app/models/centrex/service';
import { OrganizationsService } from 'src/app/services/organizations.service';
import { ExtensionDisplayPipe } from '../../../pipes/extension-display.pipe';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { MatInput } from '@angular/material/input';
import { MatFormField, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { Router, RouterLink } from '@angular/router';
import { MatButton, MatIconButton } from '@angular/material/button';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { InstanceService } from 'src/app/services/centrex/instance.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-sda',
    templateUrl: './sdas.component.html',
    styleUrls: ['./sdas.component.scss'],
    imports: [
        FaIconComponent,
        MatButton,
        RouterLink,
        MatIcon,
        ReactiveFormsModule,
        MatTable,
        MatSort,
        MatColumnDef,
        MatHeaderCellDef,
        MatHeaderCell,
        MatSortHeader,
        MatFormField,
        MatInput,
        MatIconButton,
        MatSuffix,
        MatTooltip,
        MatCellDef,
        MatCell,
        MatSelect,
        MatOption,
        MatHeaderRowDef,
        MatHeaderRow,
        MatRowDef,
        MatRow,
        MatPaginator,
        TranslateModule,
        ExtensionDisplayPipe,
    ]
})
export class SdasComponent {
    private readonly instanceService = inject(InstanceService);
    private readonly router = inject(Router);
    private destroyRef = inject(DestroyRef);

    // Icône
    public faAngleRight = faAngleRight;

    displayedColumns: string[] = ['number', 'targetType', 'target', 'actions'];
    dataSource: MatTableDataSource<Sda>;
    deleteConfirmationDialog: MatDialogRef<DialogDeleteConfirmationComponent>;

    targetTypes = Object.keys(SdaTargetTypes);
    public sdaTargetTypes = SdaTargetTypes;

    filtersForm: UntypedFormGroup;

    public extensions: Extension[] = [];
    public services: Service[] = [];

    @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;
    @ViewChild(MatSort, { static: false }) sort: MatSort;

    constructor(
        private sdaService: SdaService,
        private extensionService: ExtensionService,
        private organizationsService: OrganizationsService,
        private serviceService: ServiceService,
        private dialog: MatDialog,
        private translate: TranslateService,
        private toastr: ToastrService,
        private formbuilder: UntypedFormBuilder,
        private spinner: NgxSpinnerService
    ) {
        this.filtersForm = new UntypedFormGroup({});
        this.initFiltersForm();

        effect(() => {
            if (this.instanceService.instance()) {
                this.loadDatas();
                this.initFiltersForm();
            }
        });
    }

    /**
     * Initialisation du formulaire des filtres du tableau des SDA
     */
    initFiltersForm() {
        this.filtersForm = this.formbuilder.group({
            id: [''],
            number: [''],
            targetType: [''],
            target: [''],
        });
    }

    loadDatas() {
        this.spinner.show();

        forkJoin([this.extensionService.getExtensions(), this.serviceService.getServices(), this.sdaService.getSdas()])
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: ([extensions, services, sdas]) => {
                    this.extensions = extensions;
                    this.services = services;

                    sdas.forEach((a) => {
                        a.extension = null;
                        a.service = null;
                        if (a.targetType === this.sdaTargetTypes.extension) {
                            a.extension = this.extensions.find((b) => b.id === a.target);
                        } else if (a.targetType === this.sdaTargetTypes.service) {
                            a.service = this.services.find((b) => b.name === a.target);
                        }
                    });

                    this.dataSource = new MatTableDataSource(sdas);

                    this.dataSource.paginator = this.paginator;
                    this.dataSource.sort = this.sort;

                    this.createFilters();

                    this.spinner.hide();
                },
                error: () => {
                    this.spinner.hide();
                },
            });
    }

    createFilters() {
        this.dataSource.filterPredicate = (data: Sda, filter: string) => {
            const filterArray = filter.split('¤');
            const matchFilter = [];

            matchFilter.push(data.id && data.id.trim().toLowerCase().includes(filterArray[0].trim().toLowerCase()));
            matchFilter.push(data.number && data.number.trim().toLowerCase().includes(filterArray[1].trim().toLowerCase()));

            const targetTypeFilters = filterArray[2] ? filterArray[2].split(',') : null;

            if (targetTypeFilters) {
                matchFilter.push(data.targetType && targetTypeFilters.includes(data.targetType.trim().toLowerCase()));
            }

            if (filterArray[3] !== '') {
                if (data.targetType === SdaTargetTypes.service) {
                    matchFilter.push(data.service && data.service?.name.trim().toLowerCase().includes(filterArray[3]));
                } else {
                    matchFilter.push(
                        (data.extension?.name && data.extension?.name.trim().toLowerCase().includes(filterArray[3])) ||
                            (data.extension?.number && data.extension?.number.trim().toLowerCase().includes(filterArray[3]))
                    );
                }
            }

            return matchFilter.every(Boolean);
        };

        this.displayedColumns = ['number', 'targetType', 'target', 'actions'];

        this.applyFilters();
    }

    /**
     * Application du filtre dans le tableau des sdas
     */
    applyFilters() {
        const id = this.filtersForm.get('id').value;
        const number = this.filtersForm.get('number').value;
        const targetType = this.filtersForm.get('targetType').value;
        const target = this.filtersForm.get('target').value;

        const filterValue = (!!id ? id : '') + '¤' + (!!number ? number : '') + '¤' + (!!targetType ? targetType : '') + '¤' + (!!target ? target : '');

        this.dataSource.filter = filterValue.trim().toLowerCase();

        if (this.dataSource.paginator) {
            this.dataSource.paginator.firstPage();
        }
    }

    /**
     * Réinitialise le filtre de la colonne spécifiée.
     * @param formControlName Nom du contrôle du formulaire
     */
    resetColumnFilter(formControlName: string): void {
        this.filtersForm.controls[formControlName].reset();
        this.applyFilters();
    }

    /**
     * Réinitialise tous les filtres de colonne
     */
    resetAllColumnFilter() {
        this.filtersForm.reset();
        this.applyFilters();
    }

    onDeleteClick(sdaId: string, sdaNumber: string) {
        this.deleteConfirmationDialog = this.dialog.open(DialogDeleteConfirmationComponent, {
            panelClass: 'default-dialog',
            disableClose: true,
        });
        this.deleteConfirmationDialog.componentInstance.confirmMessage = this.translate.instant('DialogDeleteConfirmation.SdaConfirmMessage', {
            sdaNumber: sdaNumber,
        });

        this.deleteConfirmationDialog
            .afterClosed()
            .pipe(
                filter((result) => result === true),
                switchMap(() => this.sdaService.deleteSda(sdaId)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: () => {
                    this.toastr.success(
                        this.translate.instant('Sdas.SuccessMessageDeleteSda', {
                            sdaNumber: sdaNumber,
                        })
                    );
                    this.loadDatas();
                },
                error: () => {
                    this.toastr.error(
                        this.translate.instant('Sdas.ErrorMessageDeleteSda', {
                            sdaNumber: sdaNumber,
                        })
                    );
                },
                complete: () => {
                    this.deleteConfirmationDialog = null;
                },
            });
    }
}
